import React, { useMemo } from "react";
import Brand from "./Brand.jsx";
import { ReactSVG } from "react-svg";
import { setSVGColor } from "../SVGHelper.js";
import "../style/typography.scss";
import "../style/colors.scss";
import "../style/buttons.scss";
import "../style/inputs.scss";
import appsIcon from "../icon/dashboard/Grid.svg";
import dotIcon from "../icon/Dot.svg";
import Accordion from "./Accordion.jsx";
import { useLoginSession } from "../LoginSession";
import sharedConfig from "../config.js";
import InlineEditableText from "./InlineEditableText";
import apps from "../apps";

import centralBackendAxios from "../centralBackendAxios.js";

import "../style/shadows.scss";

function updatePreferredName(newPreferredName) {
    //remove breaking spaces from the name
    newPreferredName = newPreferredName.replace(/\u200B/g, "");
    //trim it
    newPreferredName = newPreferredName.trim();

    const data = { preferred_name: newPreferredName };
    return centralBackendAxios
        .put("update_user_self", data)
        .then((response) => response.data)
        .catch((error) => {
            // Rethrow with a simpler error message
            throw new Error(error.response.data.msg || "Failed to update preferred name: unknown error");
        });
}

function formatGreetingForDisplay(s) {
    s = s?.trim() || "";
    //remove previous breaking spaces
    s = s.replace(/\u200B/g, "");
    // matches any character that is not a letter (including letters from non-Latin scripts) or an apostrophe
    // adding word break hints here helps prevent forced breaking in the middle of someones name,
    // and prefers breaking on things like dashes, periods and @ (like if its an email)
    return s.replace(/([^\p{L}'’])/gu, "\u200B$1");
}

function getGreetingNameFromUserInfo(userInfo) {
    if (!userInfo) return "";
    return userInfo.preferred_name || userInfo.full_name?.split(" ")[0] || userInfo.email;
}

/**
 * MainNavDrawer Component
 *
 * Renders a navigational drawer with links to applications and displays a user greeting.
 *
 * Props:
 * - `isOpen` (boolean): Controls the visibility (open or closed state) of the drawer. Defaults to `true`.
 * - `transparent` (boolean): If true, the drawer will have a transparent background. Defaults to `false`.
 * - `overlay` (boolean): If true, the drawer will be fixed to the viewport. Defaults to `false`.
 * - `className` (string): Optional additional classes to add to the component. Defaults to an empty string.
 * - `style` (object): Optional inline styles to apply to the component, overrides default styles, without removing them completely.
 */
const MainNavDrawer = ({ isOpen = true, transparent = false, overlay = false, className = "", style = {} }) => {
    const { userInfo } = useLoginSession(false);
    const greeting = "Hello";
    const email = userInfo?.email;
    const superuser = userInfo?.superuser;

    const augmentedStyle = {
        width: isOpen ? "320px" : "0",
        transition: "width 0.3s",
        overflowX: "hidden",
        display: "flex",
        justifyContent: "flex-end",
        // transparent prop can override style prop which can override default style
        backgroundColor: "var(--colors-base-00)",
        boxShadow: "0px 0px 18px rgba(0, 0, 0, 0.2)",
        flexShrink: 0,
        height: "100%",
        position: overlay ? "fixed" : "relative",
        ...style,
        zIndex: "var(--z-index-base-high)",
        ...(transparent
            ? {
                  backgroundColor: "transparent",
                  boxShadow: "none",
              }
            : {}),
    };

    const appLinks = useMemo(() => {
        let links = [];

        // Conditionally prepend the "Hub/General" link for superusers at the top of the list
        if (userInfo?.superuser) {
            links.push({
                text: "Hub/General",
                href: sharedConfig.centralFrontendURL + "superuser",
                appRoot: "superuser",
                disabled: false,
            });
        }

        console.log(userInfo?.permissions);
        // add the app links based on iterating the apps in appsConfig
        for (const appAccessKey in apps) {
            const app = apps[appAccessKey];

            const isFHCApp = appAccessKey === "fhc";
            const hasFHCAccess = userInfo?.permissions?.includes("FHC_ACCESS");
            const hasAnyFHCPermission = userInfo?.permissions?.some((p) => p.startsWith("FHC_"));

            links.push({
                text: app.name,
                href: app.getAppLandingPage(userInfo),
                previewImage: app.previewImage,
                overviewPage: app.overviewPage,
                description: app.description,
                inquiryPage: app.inquiryPage,
                disabled:
                    !userInfo?.superuser &&
                    (!userInfo?.permissions?.includes(appAccessKey) ||
                        (isFHCApp && hasFHCAccess && !hasAnyFHCPermission)),
            });
        }

        return links;
    }, [userInfo]);

    const AppLink = ({
        text,
        href = "#",
        appRoot = "", //the root path of the app on the target url.host anything at or under this path will be considered "on" this app
        disabled = false,
    }) => {
        appRoot = appRoot.startsWith("/") ? appRoot : "/" + appRoot;
        appRoot = appRoot.endsWith("/") ? appRoot : appRoot + "/";

        const isCurrent = (() => {
            try {
                const currentUrl = new URL(window.location.href);
                const linkUrl = new URL(href);
                return currentUrl.host === linkUrl.host && (currentUrl.pathname + "/").startsWith(appRoot);
            } catch (e) {
                if (e instanceof TypeError === false) throw e;
                console.error(
                    "TypeError parsing href, make sure you aren't using a relative path (they won't work across our apps anyway) \n href:",
                    href,
                );
                return false;
            }
        })();

        return (
            <div className="flex items-center text-left">
                <ReactSVG
                    src={dotIcon}
                    alt="app icon"
                    className="mr-5"
                    beforeInjection={(svg) => {
                        svg.setAttribute("width", "20px");
                        svg.setAttribute("height", "20px");
                        setSVGColor(
                            isCurrent && !disabled
                                ? "var(--colors-primary-success-300)"
                                : "var(--colors-primary-slate-200)",
                        )(svg);
                    }}
                />
                <a href={href} className={`lnk-color ${disabled ? "disabled" : ""}`}>
                    {text}
                </a>
            </div>
        );
    };

    return (
        <div className={`${className} flex`} style={augmentedStyle}>
            <div style={{ flexGrow: 1, flexShrink: 0, width: "320px", textAlign: "center" }}>
                <div style={{ height: "48px" }}></div>
                <div style={{ textAlign: "center" }}>
                    <div
                        style={{
                            display: "inline-block",
                            width: "166px",
                            height: "166px",
                            border: "2.5px solid var(--colors-main-1)",
                            borderRadius: "50%",
                            position: "relative",
                            backgroundColor: "transparent",
                            marginBottom: "16px",
                        }}
                    >
                        <Brand
                            style={{
                                position: "absolute",
                                left: "50%",
                                top: "50%",
                                transform: "translate(-50%, -50%)",
                                padding: "0px",
                                margin: "0px",
                                maxWidth: "110px",
                                maxHeight: "110px",
                            }}
                        />
                    </div>
                </div>
                <div
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        justifyContent: "center",
                        alignItems: "center",
                        color: "var(--colors-primary-slate-600)",
                        flexWrap: "wrap",
                        padding: "0px 24px 0px 24px",
                    }}
                    className="headline-6-bold"
                >
                    {greeting}
                    {/* Add a space between*/}
                    <span style={{ whiteSpace: "pre" }}> </span>
                    <InlineEditableText
                        value={getGreetingNameFromUserInfo(userInfo)}
                        onSave={updatePreferredName}
                        format={formatGreetingForDisplay}
                        noLayoutSpaceForIcons
                    />
                </div>
                <p className="paragraph-4-regular" style={{ color: "var(--colors-primary-slate-500)" }}>
                    {email}
                </p>

                <div style={{ height: "66px" }}></div>

                <div style={{ margin: "0px 16px" }}>
                    <Accordion title={superuser ? "Applications (Admin)" : "Applications"} icon={appsIcon}>
                        <div style={{ display: "flex", flexDirection: "column", gap: "28px" }}>
                            {appLinks.map((link, index) => (
                                <AppLink key={index} {...link} />
                            ))}
                        </div>
                    </Accordion>
                </div>
            </div>
        </div>
    );
};

export default MainNavDrawer;
